import { useEffect, useRef } from 'react';
import type { ThrowEvent } from '../types';

// All units are pixels and seconds.
const GRAVITY = 2600;
/** How much speed survives the bounce off someone's card. */
const CARD_RESTITUTION = 0.42;
const FLOOR_RESTITUTION = 0.44;
/** Horizontal speed lost on each floor contact. */
const FLOOR_FRICTION = 0.7;
/** Below this vertical speed a bounce is too small to be worth drawing. */
const SETTLE_SPEED = 70;
/**
 * A shot crossing the whole screen arrives fast enough that an honest bounce
 * would fling it hundreds of pixels away. Cap the rebound so it drops onto the
 * ledge under the card it just hit.
 */
const MAX_REBOUND_X = 110;
const MAX_REBOUND_Y = 320;
/** The implied ledge sits this far below the card, in card heights. */
const FLOOR_GAP_RATIO = 0.8;
const FLIGHT_TIME = 0.62;
const REST_MS = 1000;
const FADE_MS = 380;
const MAX_LIFETIME_MS = 6000;
const RADIUS = 17;
const HIT_CLASS = 'seat-hit';

const seatElement = (memberId: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-seat="${CSS.escape(memberId)}"]`);

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

type Vector = { x: number; y: number };

/**
 * One projectile, simulated rather than tweened: it flies in from the edge of
 * the screen, bounces off the target's card, then falls onto an implied ledge
 * below it and rolls to a stop before fading.
 *
 * The launch velocity is solved backwards from the ballistic equation so the
 * arc is guaranteed to connect with the card — no matter where the seat sits.
 */
const Projectile = ({ event, onDone }: { event: ThrowEvent; onDone: (id: string) => void }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const node = ref.current;
    const seat = seatElement(event.toId);
    const card = seat?.querySelector<HTMLElement>('.seat-card') ?? seat;
    const finish = () => doneRef.current(event.id);

    // The target can leave mid-flight; drop the projectile rather than guess.
    if (!node || !seat || !card) {
      finish();
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const cardCentre = {
      x: cardRect.left + cardRect.width / 2,
      y: cardRect.top + cardRect.height / 2,
    };
    const floorY = cardRect.bottom + cardRect.height * FLOOR_GAP_RATIO;

    if (prefersReducedMotion()) {
      node.style.transform = `translate3d(${cardCentre.x}px, ${floorY}px, 0) translate(-50%, -50%)`;
      const fade = node.animate([{ opacity: 1 }, { opacity: 1 }, { opacity: 0 }], {
        duration: REST_MS + FADE_MS,
        fill: 'forwards',
      });
      fade.finished.then(finish).catch(() => undefined);
      return () => fade.cancel();
    }

    // Come in from whichever edge the thrower is closer to, so the shot still
    // reads as coming "from" them even though it starts off-screen.
    const throwerSeat = seatElement(event.fromId);
    const throwerX = throwerSeat
      ? throwerSeat.getBoundingClientRect().left + throwerSeat.getBoundingClientRect().width / 2
      : Math.random() * window.innerWidth;
    const fromLeft = throwerX <= cardCentre.x;

    // Aim at a random spot on the card face so repeat throws don't overlap.
    const impact: Vector = {
      x: cardCentre.x + randomBetween(-0.3, 0.3) * cardRect.width,
      y: cardCentre.y + randomBetween(-0.3, 0.3) * cardRect.height,
    };

    const position: Vector = {
      x: fromLeft ? -RADIUS * 2 : window.innerWidth + RADIUS * 2,
      y: floorY - randomBetween(40, 170),
    };

    // Solve v0 from x = x0 + vx·t and y = y0 + vy·t + ½g·t² for a known impact.
    const velocity: Vector = {
      x: (impact.x - position.x) / FLIGHT_TIME,
      y: (impact.y - position.y - 0.5 * GRAVITY * FLIGHT_TIME ** 2) / FLIGHT_TIME,
    };

    let spin = randomBetween(340, 900) * (fromLeft ? 1 : -1);
    let angle = 0;
    let squash = 0;
    let bouncedOffCard = false;
    let restingSince: number | undefined;
    let opacity = 1;
    let frame = 0;
    let previous = performance.now();
    const bornAt = previous;
    let hitTimer: number | undefined;

    const draw = () => {
      const scaleX = 1 + squash * 0.45;
      const scaleY = 1 - squash * 0.45;
      node.style.opacity = String(opacity);
      node.style.transform =
        `translate3d(${position.x}px, ${position.y}px, 0) translate(-50%, -50%) ` +
        `rotate(${angle}deg) scale(${scaleX}, ${scaleY})`;
    };

    /**
     * Reflects off the card on whichever axis it entered from. Penetration is
     * measured back to the face it came through — the shallower axis wins,
     * which is what makes a glancing shot skim rather than reverse.
     */
    const resolveCardHit = () => {
      const penetrationX =
        velocity.x > 0
          ? position.x - (cardRect.left - RADIUS) // came through the left face
          : cardRect.right + RADIUS - position.x; // came through the right face
      const penetrationY =
        velocity.y > 0
          ? position.y - (cardRect.top - RADIUS)
          : cardRect.bottom + RADIUS - position.y;

      if (penetrationX < penetrationY) {
        position.x += velocity.x > 0 ? -penetrationX : penetrationX;
        velocity.x = -velocity.x * CARD_RESTITUTION;
      } else {
        position.y += velocity.y > 0 ? -penetrationY : penetrationY;
        velocity.y = -velocity.y * CARD_RESTITUTION;
      }
      // A hit should always kick the thing upward a little; a dead drop off the
      // card looks like a bug rather than a bounce.
      velocity.y -= randomBetween(120, 260);
      velocity.x = Math.sign(velocity.x) * Math.min(Math.abs(velocity.x), MAX_REBOUND_X);
      velocity.y = Math.sign(velocity.y) * Math.min(Math.abs(velocity.y), MAX_REBOUND_Y);
      spin *= -0.55;
      squash = 1;
      bouncedOffCard = true;

      seat.classList.add(HIT_CLASS);
      hitTimer = window.setTimeout(() => seat.classList.remove(HIT_CLASS), 600);
    };

    const step = (now: number) => {
      // Clamp so a backgrounded tab doesn't resume with one enormous timestep.
      const dt = Math.min((now - previous) / 1000, 1 / 30);
      previous = now;

      if (restingSince === undefined) {
        velocity.y += GRAVITY * dt;
        position.x += velocity.x * dt;
        position.y += velocity.y * dt;
        angle += spin * dt;
        squash = Math.max(0, squash - dt * 6);

        const insideCard =
          !bouncedOffCard &&
          position.x > cardRect.left - RADIUS &&
          position.x < cardRect.right + RADIUS &&
          position.y > cardRect.top - RADIUS &&
          position.y < cardRect.bottom + RADIUS;
        if (insideCard) resolveCardHit();

        if (position.y >= floorY) {
          position.y = floorY;
          if (Math.abs(velocity.y) > SETTLE_SPEED) {
            velocity.y = -velocity.y * FLOOR_RESTITUTION;
            velocity.x *= FLOOR_FRICTION;
            spin *= 0.55;
            squash = Math.min(1, Math.abs(velocity.y) / 700);
          } else {
            // Settled: drop the last of the motion and start the hold.
            velocity.x = 0;
            velocity.y = 0;
            spin = 0;
            squash = 0;
            angle = angle % 360;
            restingSince = now;
          }
        }
      } else {
        const elapsed = now - restingSince;
        if (elapsed > REST_MS) opacity = Math.max(0, 1 - (elapsed - REST_MS) / FADE_MS);
      }

      draw();

      if (opacity <= 0 || now - bornAt > MAX_LIFETIME_MS) {
        finish();
        return;
      }
      frame = requestAnimationFrame(step);
    };

    draw();
    frame = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(hitTimer);
      seat.classList.remove(HIT_CLASS);
    };
  }, [event]);

  return (
    <span ref={ref} className="projectile" aria-hidden="true">
      {event.emoji}
    </span>
  );
};

export const ThrowLayer = ({
  throws,
  onRetire,
}: {
  throws: ThrowEvent[];
  onRetire: (id: string) => void;
}) => (
  <div className="throw-layer">
    {throws.map((event) => (
      <Projectile key={event.id} event={event} onDone={onRetire} />
    ))}
  </div>
);
